/**
 * RWBR — THE STOCK CAR DISPATCH, on screen. D-EZ-50.
 *
 * Howard, 18 Aug 2026: *"It shows in a modal after every race and at the
 * conclusion of the season."*
 *
 * ===========================================================================
 * IT LOOKS LIKE NEWSPRINT AND NOT LIKE THE REST OF THE APPLICATION
 * ===========================================================================
 *
 * Everything else in this game is a pit box at midday — matte dark surfaces,
 * scuffed metal, one hard warm light. This is the one screen that is a different
 * object: bone paper, black ink, a masthead rule, a serif face and two columns.
 * That is not decoration. The paper is the only artefact in the game that comes
 * from OUTSIDE the garage, and if it were styled like the standings it would
 * read as another panel of numbers rather than as something a person wrote about
 * what just happened.
 *
 * ===========================================================================
 * WHERE THE PROSE CAME FROM IS SAID, ALWAYS, IN ONE LINE
 * ===========================================================================
 *
 * At the foot of the column, in small type: the model that wrote it and whose
 * key paid, or that the game wrote it itself. A player who has pasted a key is
 * entitled to know it is being spent; a player who has not is entitled to know
 * why the writing is what it is — and neither should have to open a console to
 * find out. It is one line and it never nags.
 *
 * ⚠ THE AUTO-OPEN PREFERENCE IS THE PLAYER'S AND IT STICKS. Unticking the box
 * writes it to this browser and the modal stops appearing on its own; the button
 * on the results screen still opens it. Nothing is lost by turning it off,
 * because the edition is written and filed either way.
 */

import { useEffect, useRef } from 'react';
import type { PaperArticle } from '../lib/paper.ts';
import type { Notebook } from '../domain/paper/notebook.ts';

export interface PaperModalProps {
  article: PaperArticle | null;
  /** True while the model is being waited on. */
  loading?: boolean;
  /** Why this edition was written by the game rather than by a model. */
  reason?: string | null;
  /** Race N of M, for the flag above the masthead. */
  standing?: string;
  /**
   * D-EZ-70 — the short column beside the lead. Built from the same facts with
   * no model at all, so it is identical with a key and without one.
   */
  notes?: Notebook | null;
  /** "Vol. 3, No. 14" — the flag's own small piece of continuity. */
  volume?: string;
  onClose: () => void;
  /** Buy a fresh one. Absent when there is no model to buy it from. */
  onRewrite?: () => void;
  /** D-EZ-102 — what the rewrite button says (the newsstand asks for a first one). */
  rewriteLabel?: string;
}

export default function PaperModal({
  article,
  loading = false,
  reason = null,
  standing,
  notes = null,
  volume,
  onClose,
  onRewrite,
  rewriteLabel = 'Write it again',
}: PaperModalProps) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!article && !loading) return null;

  return (
    <div className="paper__scrim" role="presentation" onClick={onClose}>
      <div
        className="paper"
        role="dialog"
        aria-modal="true"
        aria-label="The Stock Car Dispatch"
        tabIndex={-1}
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="paper__x" onClick={onClose} aria-label="Close the paper">
          ×
        </button>

        {loading && !article ? (
          <div className="paper__press">
            <div className="paper__pressline">THE PRESS IS RUNNING</div>
            <p>Somebody is writing up the race.</p>
          </div>
        ) : article ? (
          <>
            <header className="paper__flag">
              <div className="paper__masthead">{article.masthead}</div>
              <div className="paper__motto">
                <span>{article.motto}</span>
                <span>
                  {article.edition === 'winter'
                    ? 'Winter edition'
                    : article.edition === 'season'
                      ? 'Season in review'
                      : 'Monday edition'}
                  {standing ? ` · ${standing}` : ''}
                  {volume ? ` · ${volume}` : ''}
                </span>
              </div>
            </header>

            <h1 className="paper__headline">{article.headline}</h1>
            {article.standfirst && <p className="paper__standfirst">{article.standfirst}</p>}

            <div className="paper__byline">
              <b>By {article.byline}</b>
              {article.dateline && <span className="paper__dateline">{article.dateline}</span>}
            </div>

            <div className="paper__body">
              {article.paragraphs.map((p, i) => (
                <p key={i} className={i === 0 ? 'paper__lede' : undefined}>
                  {p}
                </p>
              ))}
            </div>

            {/*
              D-EZ-70 — THE NOTEBOOK.

              A real racing weekly does not put one article on the page. These
              are the week's odds and ends — who missed the show, who moved in
              the standings, what the garage did — and every one of them is a
              fact the lead article was told to leave out. Two editions with
              similar leads read differently when the column beside them is full
              of different names.

              ⚠ AND IT SAYS WHAT IT DROPPED. Six is the column; a week with
              eleven items says eleven.
            */}
            {notes && notes.items.length > 0 && (
              <aside className="paper__notes" aria-label="The notebook">
                <div className="paper__noteshead">The Notebook</div>
                <dl>
                  {notes.items.map((n) => (
                    <div key={n.head + n.text} className="paper__note">
                      <dt>{n.head}</dt>
                      <dd>{n.text}</dd>
                    </div>
                  ))}
                </dl>
                {notes.total > notes.items.length && (
                  <p className="paper__notesmore">
                    {notes.items.length} of {notes.total} items.
                  </p>
                )}
              </aside>
            )}

            <footer className="paper__foot">
              <span className="paper__source">
                {article.source === 'ai' ? (
                  <>
                    Written by {article.model ?? 'the model'}
                    {article.paidBy === 'byo' ? ' on your API key.' : '.'}
                  </>
                ) : (
                  <>Written by the game — no model was called.{reason ? ` ${capitalise(reason)}.` : ''}</>
                )}
              </span>

              <span className="paper__actions">
                {onRewrite && (
                  <button type="button" className="btn btn--quiet btn--tiny" onClick={onRewrite} disabled={loading}>
                    {loading ? 'Writing…' : rewriteLabel}
                  </button>
                )}
                <button type="button" className="btn btn--tiny" onClick={onClose}>
                  Done
                </button>
              </span>
            </footer>
          </>
        ) : null}
      </div>
    </div>
  );
}

const capitalise = (s: string): string => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));
